import React, { useState } from 'react';
import CustomInput from '../../components/form/CustomInput';
import { useNavigate } from 'react-router-dom';
import CustomButtonConfirm from '../../components/form/CustomButtonConfirm';
import { loginSuccess } from '../../store/auth/login/action';
import * as Yup from "yup";
import { useFormik } from "formik";
import { useDispatch } from 'react-redux';
import { postLoginToken, resetPassword } from '../../helpers/configRoutes/backend_helper';

const Login = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const [forgotPassword, setForgotPassword] = useState(false);
    const [loading, setLoading] = useState(false);

    const validation = useFormik({
        initialValues: {
            email: '',
            password: '',
        },
        validationSchema: Yup.object({
            email: Yup.string()
                .email("Invalid email address")
                .required("Please enter your email"),
            password: Yup.string().when('forgotPassword', {
                is: false,
                then: Yup.string().required("Please enter your password"),
            }),
        }),
        onSubmit: async (values, { setErrors }) => {
            setLoading(true);

            if (forgotPassword) {
                const response = await resetPassword(values);
                if (response.status === true) {
                    setForgotPassword(false);
                }
            } else {
                const response = await postLoginToken(values);
                if (response.status === true) {
                    dispatch(loginSuccess(response));
                    navigate(response.user.models[0].link);
                } else {
                    if (response.errors?.length) {
                        setErrors(response.errors);
                    }
                }
            }

            setLoading(false);
        },
    });

    return (
        <div className="min-h-screen w-full bg-cs-bg-alt flex items-center justify-center px-4">
            <div className="w-full max-w-5xl bg-cs-bg rounded-3xl shadow-2xl overflow-hidden grid grid-cols-1 md:grid-cols-2 border border-cs-border">
                {/* Left side - branding */}
                <div className="hidden md:flex flex-col justify-between bg-cs-primary-soft px-10 py-8 relative">
                    <div>
                        {/* Logo */}
                        <div className="flex items-center gap-3">
                            <div className="flex w-140 h-140 items-center justify-center">
                                <img src="logoA.png"/>
                            </div>
                        </div>

                        <h1 className="mt-1 text-3xl font-semibold text-cs-dark leading-snug">
                            Smart control for your logistics operations.
                        </h1>
                        <p className="mt-2 text-sm text-cs-muted">
                            Access your dashboard to manage shipments, clients, and reports all in one place — with security and clarity.
                        </p>
                    </div>

                    <div className="mt-10 space-y-2 text-xs text-cs-muted">
                        <p>• Real-time secure platform</p>
                        <p>• Integrates with your existing tools</p>
                        <p>• Dedicated CargoSmart support</p>
                    </div>

                    <div className="pointer-events-none absolute inset-0 opacity-5 bg-[radial-gradient(circle_at_top,_#FF7A00_0,_transparent_55%)]" />
                </div>

                {/* Right side - form */}
                <div className="flex flex-col justify-center px-8 py-10">
                    {/* Header */}
                    <div className="mb-8">
                        <p className="text-xs uppercase tracking-[0.18em] text-cs-muted mb-2">
                            Welcome back
                        </p>
                        <h2 className="text-2xl font-semibold text-cs-dark">
                            Sign in to your account
                        </h2>
                        <p className="text-sm text-cs-muted mt-2">
                            Enter your credentials to continue to your CargoSmart dashboard.
                        </p>
                    </div>

                    {/* Forgot password info */}
                    {forgotPassword && (
                        <div className="mb-4 text-center">
                            <p className="text-sm font-semibold text-cs-dark">
                                Forgot your password?
                            </p>
                            <p className="text-xs text-cs-muted">
                                Enter the email address associated with your account to reset it.
                            </p>
                        </div>
                    )}

                    <form
                        className="space-y-4"
                        onSubmit={(e) => {
                            e.preventDefault();
                            validation.handleSubmit();
                            return false;
                        }}
                    >
                        {/* Email */}
                        <div>
                            <CustomInput
                                label="Email address"
                                type="email"
                                name="email"
                                labelClassName="text-sm font-medium text-cs-dark mb-1"
                                error={validation.errors.email}
                                value={validation.values.email}
                                handleChange={validation.handleChange}
                                className="w-full"
                            />
                        </div>

                        {/* Password */}
                        {!forgotPassword && (
                            <div>
                                <CustomInput
                                    label="Password"
                                    type="password"
                                    name="password"
                                    forgotPassword={true}
                                    labelClassName="text-sm font-medium text-cs-dark mb-1"
                                    error={validation.errors.password}
                                    value={validation.values.password}
                                    handleChange={validation.handleChange}
                                    className="w-full"
                                />
                            </div>
                        )}

                        {/* Toggle forgot password */}
                        <div className="flex items-center justify-between text-xs mt-2">
                            <button
                                type="button"
                                onClick={() => setForgotPassword(!forgotPassword)}
                                className="text-cs-primary hover:text-cs-primary-hover transition-colors"
                            >
                                {forgotPassword
                                    ? "Back to login"
                                    : "Forgot your password?"}
                            </button>
                        </div>

                        {/* Submit */}
                        <div className="pt-4">
                            <CustomButtonConfirm
                                loading={loading}
                                textLoad="Processing..."
                                type="success"
                                className="w-full bg-cs-primary hover:bg-cs-primary-hover text-white font-semibold py-3 rounded-2xl transition-all shadow-md hover:shadow-lg"
                                text={forgotPassword ? "Reset password" : "Sign in"}
                            />
                        </div>

                        {/* Support */}
                        <p className="pt-4 text-center text-xs text-cs-muted">
                            Having trouble accessing your account?
                            <span
                                onClick={() => console.log('Support contact')}
                                className="ml-1 text-cs-primary hover:text-cs-primary-hover cursor-pointer font-medium"
                            >
                                Contact support
                            </span>
                        </p>
                    </form>
                </div>
            </div>
        </div>
    );
};

export default Login;
